export type ContentBlockType = 'paragraph' | 'heading' | 'callout' | 'image' | 'video';

export interface BaseContentBlock {
  id: string;
  type: ContentBlockType;
}

export interface ParagraphBlock extends BaseContentBlock {
  type: 'paragraph';
  content: string;
}

export interface HeadingBlock extends BaseContentBlock {
  type: 'heading';
  level: 2; // We only use H2 for section headings
  content: string;
}

export interface CalloutBlock extends BaseContentBlock {
  type: 'callout';
  title?: string;
  content: string;
}

export interface ImageBlock extends BaseContentBlock {
  type: 'image';
  url: string;
  alt: string;
  caption?: string;
}

export interface VideoBlock extends BaseContentBlock {
  type: 'video';
  url: string;
  title?: string;
}

export type ContentBlock = ParagraphBlock | HeadingBlock | CalloutBlock | ImageBlock | VideoBlock;

export interface HelpArticleContent {
  blocks: ContentBlock[];
}

export interface HelpArticle {
  id: string;
  title: string;
  slug: string;
  category: string;
  subtitle?: string;
  content: HelpArticleContent;
  footer_content?: string;
  status: 'draft' | 'published';
  author_id: string;
  created_at: string;
  updated_at: string;
  published_at?: string;
}

export interface HelpCategory {
  id: string;
  name: string;
  slug: string;
  description?: string;
  display_order: number;
  created_at: string;
}

// Generate a URL-friendly slug from a title
export function generateSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// Create a new empty content block
export function createContentBlock(type: ContentBlockType): ContentBlock {
  const baseBlock = {
    id: crypto.randomUUID(),
    type
  };

  switch (type) {
    case 'paragraph':
      return { ...baseBlock, type: 'paragraph', content: '' };
    case 'heading':
      return { ...baseBlock, type: 'heading', level: 2 as const, content: '' };
    case 'callout':
      return { ...baseBlock, type: 'callout', content: '', title: '' };
    case 'image':
      return { ...baseBlock, type: 'image', url: '', alt: '' };
    case 'video':
      return { ...baseBlock, type: 'video', url: '' };
  }
}
